import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { g, p, pu, eur } from "@/lib/api";
import { useI18n } from "@/i18n";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Plus, Search, Cake } from "lucide-react";

const empty = { name: "", phone: "", email: "", birthday: "", anniversary: "", address: "", fiscal_code: "", notes: "", allergens: [], requests: [], favourite_wine: "" };
const CHIP = (v) => (Array.isArray(v) ? v.join(", ") : v || "");
const LISTA = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);

export default function Clienti() {
  const { t, lang } = useI18n();
  const [list, setList] = useState([]);
  const [q, setQ] = useState("");
  const [form, setForm] = useState(empty);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);

  const load = useCallback(() => g("/customers", { q }).then(setList), [q]);
  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!form.name) return toast.error(t("cl.needName"));
    if (editId) await pu(`/customers/${editId}`, form);
    else await p("/customers", form);
    toast.success(t("cl.saved"));
    setOpen(false);
    setForm(empty);
    setEditId(null);
    load();
  };

  const day = (d) => new Date(d).toLocaleDateString(lang);

  return (
    <div className="fade-up space-y-5" data-testid="clienti-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-slate-500">{t("g.owner")}</p>
          <h1 className="mt-1 text-3xl font-bold tracking-tight sm:text-4xl">{t("cl.title")}</h1>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button className="h-11" data-testid="add-customer-btn">
              <Plus className="mr-1.5 h-4 w-4" /> {t("cl.new")}
            </Button>
          </DialogTrigger>
          <DialogContent data-testid="customer-dialog">
            <DialogHeader>
              <DialogTitle>{editId ? t("cl.editTitle") : t("cl.new")}</DialogTitle>
            </DialogHeader>
            <div className="space-y-2">
              {[["name", t("cl.name")], ["phone", t("cl.phone")], ["email", t("cl.email")],
                ["address", t("cl.address")], ["fiscal_code", t("cl.fiscal")], ["notes", t("cl.notes")]].map(([k, l]) => (
                <div key={k}>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{l}</p>
                  <Input className="mt-1" value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} data-testid={`cust-${k}-input`} />
                </div>
              ))}
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Allergie note (separate da virgola)</p>
                <Input className="mt-1" placeholder="glutine, crostacei" value={CHIP(form.allergens)}
                       onChange={(e) => setForm({ ...form, allergens: LISTA(e.target.value) })} data-testid="cust-allergens-input" />
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Richieste ricorrenti</p>
                <Input className="mt-1" placeholder="seggiolone, tavolo tranquillo" value={CHIP(form.requests)}
                       onChange={(e) => setForm({ ...form, requests: LISTA(e.target.value) })} data-testid="cust-requests-input" />
              </div>
              <div>
                <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Vino preferito</p>
                <Input className="mt-1" value={form.favourite_wine || ""}
                       onChange={(e) => setForm({ ...form, favourite_wine: e.target.value })} data-testid="cust-wine-input" />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">{t("cl.birthday")}</p>
                  <Input className="mt-1" type="date" value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} data-testid="cust-birthday-input" />
                </div>
                <div>
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Anniversario</p>
                  <Input className="mt-1" type="date" value={form.anniversary || ""} onChange={(e) => setForm({ ...form, anniversary: e.target.value })} data-testid="cust-anniversary-input" />
                </div>
              </div>
              <Button className="h-11 w-full" onClick={submit} data-testid="save-customer-btn">
                {t("g.save")}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
        <Input className="h-10 pl-9" placeholder={t("cl.searchPh")} value={q} onChange={(e) => setQ(e.target.value)} data-testid="customer-search-input" />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
        {list.map((c) => (
          <Card key={c.id} className="p-5" data-testid={`customer-${c.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-semibold">{c.name}</p>
                <p className="truncate text-xs text-slate-500">{c.phone} {c.email && `· ${c.email}`}</p>
                {c.birthday && (
                  <p className="mt-1 flex items-center gap-1 text-xs text-slate-500">
                    <Cake className="h-3 w-3" /> {day(c.birthday)}
                  </p>
                )}
                {c.notes && <p className="mt-1 text-xs text-amber-700">{c.notes}</p>}
                {(c.allergens?.length > 0 || c.requests?.length > 0) && (
                  <div className="mt-1 flex flex-wrap gap-1" data-testid={`customer-scheda-${c.id}`}>
                    {(c.allergens || []).map((a) => (
                      <span key={a} className="rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-900">{a}</span>
                    ))}
                    {(c.requests || []).map((a) => (
                      <span key={a} className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold text-slate-600">{a}</span>
                    ))}
                  </div>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                data-testid={`edit-customer-${c.id}`}
                onClick={() => {
                  setForm({ ...empty, ...c });
                  setEditId(c.id);
                  setOpen(true);
                }}
              >
                {t("g.edit")}
              </Button>
            </div>
            <div className="mt-4 flex gap-6 border-t border-slate-100 pt-3">
              {[["visits", t("cl.visits"), c.visits], ["spent", t("cl.spent"), eur(c.spent)],
                ["avg", t("cl.avg"), eur(c.visits ? c.spent / c.visits : 0)]].map(([id, l, v]) => (
                <div key={id}>
                  <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-slate-500">{l}</p>
                  <p className="font-num text-base font-bold">{v}</p>
                </div>
              ))}
            </div>
            {c.history?.length > 0 && (
              <p className="mt-3 text-[11px] text-slate-500">
                {t("cl.last", {
                  date: day(c.history[c.history.length - 1].date),
                  total: eur(c.history[c.history.length - 1].total),
                })}
              </p>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
}
